import {
  CreateStatement,
  EntityDto,
  GetStatementInvoiceOptions,
  LogStatementPayment,
  PagedStatementRequest,
  SendStatement,
  Statement,
  StatementInvoice,
  StatementService,
  UpdateStatement,
} from '@services';
import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

class StatementStore {
  statement: Statement | undefined;
  statements: Map<string, Statement> = new Map();
  totalCount: number = 0;
  skipCount: number = 0;
  currentPage: number = 1;
  maxResultCount: number = 15;
  loading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  async getAll(pagedFilterAndSortedRequest: PagedStatementRequest) {
    this.setLoading(true);
    try {
      const result = await StatementService.getAll(pagedFilterAndSortedRequest);
      runInAction(() => {
        this.statements.clear();
        result.items.forEach((statement: Statement) => {
          this.statements.set(statement.id, statement);
        });
        this.totalCount = result.totalCount;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async get(entityDto: EntityDto<string>) {
    this.setLoading(true);
    try {
      const result = await StatementService.get(entityDto);
      runInAction(() => {
        this.statement = result;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getPublicStatement(entityDto: EntityDto<string>) {
    this.setLoading(true);
    try {
      const result = await StatementService.getPublicStatement(entityDto);
      runInAction(() => {
        this.statement = result;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async create(input: CreateStatement): Promise<Statement | null> {
    this.setLoading(true);
    try {
      const result = await StatementService.create(input);
      this.setLoading(false);
      message.success({ content: 'Statement was successfully created!' });
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      return null;
    }
  }

  async update(input: UpdateStatement): Promise<Statement | null> {
    this.setLoading(true);
    try {
      const result = await StatementService.update(input);
      runInAction(() => {
        this.statement = result;
        this.statements.set(result.id, result);
      });
      this.setLoading(false);
      message.success({ content: 'Statement was updated!' });
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      return null;
    }
  }

  async delete(entityDto: EntityDto<string>): Promise<boolean> {
    this.setLoading(true);
    try {
      await StatementService.delete(entityDto);
      runInAction(() => {
        this.statements.delete(entityDto.id);
      });
      this.setLoading(false);
      message.success({ content: 'Statement was deleted!' });
      return true;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      return false;
    }
  }

  async logPayment(input: LogStatementPayment): Promise<Statement | null> {
    try {
      const result = await StatementService.logPayment(input);
      runInAction(() => {
        this.statement = result;
        this.statements.set(result.id, result);
      });
      message.success({ content: 'Payment was successfully logged!' });
      return result;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async send(input: SendStatement): Promise<boolean> {
    try {
      await StatementService.send(input);
      message.success({ content: 'Statement sent!' });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async getInvoiceOptions(input: GetStatementInvoiceOptions): Promise<StatementInvoice[]> {
    try {
      return await StatementService.getInvoiceOptions(input);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  clearStatement() {
    runInAction(() => {
      this.statement = undefined;
    });
  }

  clearStatements() {
    runInAction(() => {
      this.statements.clear();
    });
  }

  resetStatements = () => {
    runInAction(() => {
      this.statements.clear();
      this.totalCount = 0;
      this.skipCount = 0;
      this.currentPage = 1;
    });
  };

  setMaxResultCount = (value: number) => {
    runInAction(() => {
      this.maxResultCount = value;
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };
}

export default StatementStore;
